/** The map legend: habitat colours by biome (only those in the plan), zones, water, paths and icons. */
import { useMemo } from 'react';
import { isAnimalArea } from '../../../../core/model';
import type { Biome, IconType } from '../../../../shared/types';
import { BIOMES, ICONS, LIGHT } from '../../../render/palette';
import { useEditor, type EditorStore } from '../../../store/editor';

/** The legend panel. */
export function Legend({ store }: { store: EditorStore }) {
  const doc = useEditor(store, (s) => s.doc);
  const species = useEditor(store, (s) => s.species);
  const { biomes, kinds, icons } = useMemo(() => {
    const byId = new Map((species ?? []).map((s) => [s.id, s]));
    const biomes = new Set<Biome>(), kinds = new Set<string>(), icons = new Set<IconType>();
    for (const a of Object.values(doc.areas)) {
      kinds.add(a.kind);
      if (isAnimalArea(a) && a.kind === 'habitat') { const sp = byId.get(a.speciesId); if (sp) biomes.add(sp.biome); }
    }
    for (const ic of Object.values(doc.icons)) icons.add(ic.type);
    return { biomes, kinds, icons };
  }, [doc, species]);
  const sw = (color: string) => <i style={{ background: color }} />;
  return (
    <section className="panel">
      <h3>Legend</h3>
      <div className="legend">
        {(Object.keys(BIOMES) as Biome[]).filter((b) => biomes.has(b)).map((b) => (
          <span key={b}>{sw(BIOMES[b].color)}{BIOMES[b].label}</span>
        ))}
        {kinds.has('aviary') && <span>{sw('var(--map-aviary)')}🐦 Aviary</span>}
        {kinds.has('aquarium') && <span>{sw('var(--map-aquarium)')}🐠 Aquarium</span>}
        <span>{sw('var(--map-water)')}Water</span>
        <span>{sw(LIGHT.road)}Path</span>
        <span>{sw(LIGHT.roadLoose)}Path not joined up</span>
        <span>{sw(LIGHT.timber)}Bridge</span>
        {kinds.has('plaza') && <span>{sw('var(--map-plaza)')}Entrance plaza</span>}
        {kinds.has('backOfHouse') && <span>{sw('var(--map-boh)')}Staff area</span>}
        <span><i style={{ background: 'transparent', borderColor: '#e74c3c', borderWidth: 2 }} />Has a problem</span>
        {(Object.keys(ICONS) as IconType[]).filter((t) => icons.has(t)).map((t) => (
          <span key={t}>{ICONS[t].icon} {ICONS[t].label}</span>
        ))}
      </div>
    </section>
  );
}
